import { ChevronRight } from 'lucide-react'
import ModalBackdrop from './ModalBackdrop'
import { ProfileAvatar } from './ProfileAvatar'
import QuestEmptyShield from './QuestEmptyShield'
import TierMark from './profile/TierMark'
import OtherPlayerCallsGrid, { type FixtureState } from './OtherPlayerCallsGrid'
import { CHIP_ICONS } from './chipIcons'
import { getClubBanner } from '../clubBanners'
import { chipDefinitions } from '../mockChips'
import { computeGameweekDetail } from '../season'
import type { CalibrationState } from '../calibration'
import type { ChipId, Game, Prediction, SeasonStats, UserProfile } from '../types'
import './TopScorerModal.css'

interface TopScorerModalProps {
  gameweek: number
  profile: UserProfile
  seasonStats: SeasonStats
  calibration: CalibrationState
  games: Game[]
  predictions: Record<string, Prediction>
  bankerGameId: string | null
  activeChipId: ChipId | null
  fixtureStates: Record<string, FixtureState>
  onClose: () => void
  /** Open this player's profile. The modal closes on the way — the profile is a pushed screen,
   *  and leaving an overlay behind it would put a stranger's week over their own page. */
  onOpenProfile: () => void
}

/**
 * Who is top of the gameweek, and how they got there — opened from the Predict hero's Highest
 * tile.
 *
 * It answers the question where it was asked. The tile used to push this player's whole profile
 * and scroll it down to their calls, which meant leaving the gameweek to read one week of it.
 *
 * Everything on it is that player's own server-held data: their avatar, club and Intuition Rating
 * tier come from their profile, their picks from their rows. Nothing is derived from the viewer.
 */
export default function TopScorerModal({
  gameweek,
  profile,
  seasonStats,
  calibration,
  games,
  predictions,
  bankerGameId,
  activeChipId,
  fixtureStates,
  onClose,
  onOpenProfile,
}: TopScorerModalProps) {
  const chip = activeChipId
    ? chipDefinitions.find((c) => c.id === activeChipId)
    : undefined
  const ChipIcon = activeChipId ? CHIP_ICONS[activeChipId] : null

  // Only fixtures with a score count, so the total climbs through the weekend rather than
  // jumping at full time. Summing the cards would be wrong — the Banker and the chips change the
  // arithmetic — so it comes from the same function every other total in the app comes from.
  const results: Record<string, { homeScore: number; awayScore: number }> = {}
  for (const game of games) {
    const fixture = fixtureStates[game.id]
    if (fixture?.kickedOff && fixture.score) results[game.id] = fixture.score
  }
  const hasResults = Object.keys(results).length > 0
  const totalPoints = hasResults
    ? computeGameweekDetail(
        gameweek,
        games,
        predictions,
        results,
        bankerGameId,
        activeChipId === 'doubleUp',
        activeChipId === 'cleanSweep',
        activeChipId === 'goalfest'
      ).totalPoints
    : null

  const banner = getClubBanner(profile.favouriteTeam)

  return (
    <ModalBackdrop onClose={onClose} scrim="immersive">
      {(close) => (
        <div className="top-scorer">
          <button
            type="button"
            className="top-scorer__close"
            onClick={() => close()}
            aria-label="Close"
          >
            ✕
          </button>

          <p className="top-scorer__eyebrow label-eyebrow">
            Highest · Gameweek {gameweek}
          </p>

          <div className="top-scorer__identity">
            <ProfileAvatar
              preset={profile.avatarPreset ?? null}
              initials={profile.initials}
              className="top-scorer__avatar"
            />

            <div className="top-scorer__who">
              {/* The one control in the header: the crest and the avatar stay inert so there is
                  exactly one way through to their profile. */}
              <button
                type="button"
                className="top-scorer__name"
                onClick={() => close(onOpenProfile)}
              >
                {profile.name}
                <ChevronRight className="top-scorer__name-chevron" aria-hidden="true" />
              </button>
              <TierMark seasonStats={seasonStats} calibration={calibration} />
            </div>

            {banner ? (
              <img className="top-scorer__crest" src={banner} alt="" aria-hidden="true" />
            ) : (
              <QuestEmptyShield className="top-scorer__crest" size={44} />
            )}
          </div>

          <div className="top-scorer__hero">
            {chip && ChipIcon && (
              <div className="top-scorer__chip">
                <span className="top-scorer__chip-badge">
                  <ChipIcon aria-hidden="true" />
                </span>
                <span className="top-scorer__chip-name label-eyebrow">{chip.name}</span>
              </div>
            )}
            {/* A zero before anything has kicked off reads as a verdict rather than an empty
                week — same rule as the profile's calls section. */}
            {totalPoints !== null && (
              <p className="top-scorer__points">{totalPoints} pts</p>
            )}
          </div>

          <OtherPlayerCallsGrid
            games={games}
            predictions={predictions}
            bankerGameId={bankerGameId}
            fixtureStates={fixtureStates}
          />
        </div>
      )}
    </ModalBackdrop>
  )
}
